import React, { useEffect } from "react";
import { AssetsSidebar } from "./AssetsSidebar";
import { useSidebarContext } from "../SidebarContext";
import { useFetchAssets } from "./hooks/useFetchAssets"; // Import the custom hook
import configs from "../../utils/configs";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import PropTypes from "prop-types";
import { useArtworks } from "./hooks/useArtworks";
import { LogMessageType } from "../../utils/messages";

export function AssetsSidebarContainer({ scene, signedIn, initialTab = null, userName, sessionId }) {
  const { setSidebar } = useSidebarContext();
  const {
    assets,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fileTypeFilter,
    handleFileTypeFilterChange,
    fileTypeOptions,
    renameAsset,
    deleteAsset,
    tabs,
    selectedTab,
    handleSelectedTabChange,
    sourceOptions,
    handleSourceChange,
    source,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    editable,
    onChangePlaceLoading,
    placeLoading
  } = useFetchAssets({
    assetsEndpoint: configs.CUSTOM_API_URL + "/assets360",
    publicDomainArtEndpoint: configs.PUBLIC_ART_API,
    librariesApiKey: configs.SKETCHFAB_API_KEY,
    defaultToken: null,
    signedIn
  }); // Use the custom hook

  const { getPublicDomainArtwork } = useArtworks();

  useEffect(() => {
    if (initialTab) {
      handleSelectedTabChange(initialTab);
    }
  }, [initialTab, handleSelectedTabChange]);

  const processThumbnailUrl = thumbnailUrl => {
    return proxiedUrlFor(thumbnailUrl);
  };

  const proxyUrl = url => {
    if (selectedTab === "public-domain-art") {
      return proxiedUrlFor(url);
    }

    return url;
  };

  const onSelectAssets = async assets => {
    onChangePlaceLoading(true);
    // Create an array of promises to handle URL generation
    const urlPromises = assets.map(async asset => {
      let urlForProxy =
        asset?.artwork && asset?.artwork?.artwork_model ? asset?.artwork?.artwork_model : proxyUrl(asset.url);
      if (asset?.artwork && !asset?.artwork?.artwork_model) {
        const artwork = await getPublicDomainArtwork(asset);
        if (artwork?.artwork_model) {
          urlForProxy = artwork?.artwork_model;
        }
      }
      return { ...asset, urlForProxy };
    });

    // Wait for all URLs to be generated
    const assetsWithUrls = await Promise.all(urlPromises);

    // Proceed with the forEach loop
    assetsWithUrls.forEach((asset, index) => {
      const urlForProxy = asset.urlForProxy;
      scene.emit("action_selected_media_result_entry", {
        entry: {
          ...asset,
          type: "media",
          url: urlForProxy,
          thumbnail: proxyUrl(asset.thumbnail),
          index
        },
        selectAction: "spawn"
      });
    });

    onChangePlaceLoading(false);

    // Send a delayed message to the user that the asset was placed
    setTimeout(() => {
      window.APP.messageDispatch.push(
        {
          sender: userName,
          senderSessionId: sessionId
        },
        "object"
      );
    }, 1000);
  };

  return (
    <AssetsSidebar
      items={assets}
      loading={loading}
      error={error}
      open={true}
      onClose={() => setSidebar("")}
      processThumbnailUrl={processThumbnailUrl}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      fileTypeFilter={fileTypeFilter}
      handleFileTypeFilterChange={handleFileTypeFilterChange}
      fileTypeOptions={fileTypeOptions}
      onRename={renameAsset}
      onDelete={deleteAsset}
      tabs={tabs}
      selectedTab={selectedTab}
      handleSelectedTabChange={handleSelectedTabChange}
      sourceOptions={sourceOptions}
      handleSourceChange={handleSourceChange}
      source={source}
      hasNext={hasNext}
      hasPrev={hasPrev}
      onNext={onNext}
      onPrev={onPrev}
      editable={editable}
      onSelectAssets={onSelectAssets}
      placeLoading={placeLoading}
      onChangePlaceLoading={onChangePlaceLoading}
      userName={userName}
      sessionId={sessionId}
    />
  );
}

AssetsSidebarContainer.propTypes = {
  scene: PropTypes.object,
  initialTab: PropTypes.string,
  signedIn: PropTypes.bool
};
